<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed } from "vue";
import { Validator } from "@zyzgroup/core-common";
import { mapState } from "../../context/context";
import Text from "./Text.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: { Text },
  inheritAttrs: true
});

const props = withDefaults(
  defineProps<{
    type?: string; // text email phone ...
    required?: boolean;
    defaultValue?: string;

    placeholder?: string;
    placeholderType?: "normal" | "underline" | "jump" | "jump-each";
    duration?: number;

    showError?: boolean;
    validator?: Validator;
  }>(),
  {
    type: "text",
    required: false,

    placeholder: "",
    placeholderType: "normal",
    duration: 500,

    showError: true
  }
);

const emits = defineEmits<{
  (event: "focus"): void;
  (event: "yz-focus"): void;
  (event: "blur"): void;
  (event: "yz-blur"): void;
  (event: "input", text: string): void;
  (event: "yz-input", text: string): void;
  (event: "error", text: string): void;
  (event: "yz-error", text: string): void;
}>();

const mappedState = mapState([
  "color.redCommon",
  "color.gapPattern",
  "color.placeholderPattern",
  "color.primaryPattern",
  "color.fontPattern"
]);

const isTypeNormal = computed(() => props.placeholderType == "normal");
const isTypeUnderline = computed(() => props.placeholderType == "underline");
const isTypeJump = computed(() => props.placeholderType == "jump");
const isTypeJumpEach = computed(() => props.placeholderType == "jump-each");

const placeholders = props.placeholder.split("");
const placeholderJumpEachDuration = props.duration / placeholders.length;

const el = ref();

const isFocus = ref(false);
const errorText = ref("");
const inputText = ref(props.defaultValue || "");

function focus() {
  isFocus.value = true;
  emits("focus");
  emits("yz-focus");
}

function blur() {
  isFocus.value = false;
  emits("blur");
  emits("yz-blur");
  validate();
}

function input(e: Event) {
  inputText.value = (e.target as HTMLInputElement).value;
  validate();
}

function validate() {
  if (inputText.value && props.validator) {
    const result = props.validator(inputText.value);
    if (!result.valid) {
      errorText.value = result.errors[0];
      emits("error", errorText.value);
      emits("yz-error", errorText.value);
      emits("input", "");
      emits("yz-input", "");
      return;
    }
  }
  errorText.value = "";
  if (inputText.value) {
    emits("input", inputText.value);
    emits("yz-input", inputText.value);
  }
}

onMounted(() => {
  if (props.defaultValue) {
    el.value.value = props.defaultValue;
  }
});

onUnmounted(() => {});

defineExpose({});
</script>

<template>
  <div class="input">
    <!-- error prompt top -->
    <div
      v-if="props.showError && errorText && !isTypeUnderline"
      class="error-prompt-top"
    >
      <Text
        type="warn"
        :color="mappedState['color.redCommon'].value"
      >
        {{ errorText }}
      </Text>
    </div>
    <!-- input-wrap -->
    <div
      class="input-wrap"
      :class="isFocus || inputText ? 'focus' : ''"
      :style="{
        fontSize: '1em',
        border: `${isTypeUnderline ? '0' : '1'}px solid transparent`,
        borderBottom: `1px solid transparent`,
        borderRadius: isTypeUnderline
          ? '0'
          : `var(--yz-input-border-radius, 4px)`,
        borderColor: errorText
          ? mappedState['color.redCommon'].value
          : isFocus
          ? mappedState['color.primaryPattern'].value
          : inputText || isTypeNormal || isTypeUnderline
          ? mappedState['color.gapPattern'].value
          : 'transparent',
        transition: props.duration + 'ms'
      }"
    >
      <!-- placeholder-wrap -->
      <div
        class="placeholder"
        :style="{
          lineHeight:
            isFocus || inputText ? 1.3 : `var(--yz-input-height, 44px)`,
          color: mappedState['color.placeholderPattern'].value,
          transition:
            isTypeNormal || isTypeUnderline ? '' : props.duration + 'ms',
          transform:
            isTypeNormal || isTypeUnderline
              ? ''
              : isFocus || inputText
              ? `translateY(-100%)`
              : 'none'
        }"
      >
        <!-- placeholder: normal / underline -->
        <div
          v-if="
            !errorText &&
            (isTypeNormal || isTypeUnderline) &&
            !isFocus &&
            !inputText
          "
          :style="{
            paddingLeft: '0.5em'
          }"
        >
          {{ props.placeholder }}
        </div>
        <!-- placeholder: jump -->
        <div v-if="!errorText && isTypeJump && !inputText">
          {{ props.placeholder }}
        </div>
        <!-- placeholder: jump-each -->
        <span
          v-if="!errorText && isTypeJumpEach && !inputText"
          v-for="(item, index) in placeholders"
          :key="index"
          :style="{
            transition:
              props.duration + 'ms cubic-bezier(0.68, -0.55, 0.265, 1.55)',
            transitionDelay: `${placeholderJumpEachDuration * index}ms`,
            transform:
              isFocus || inputText
                ? `translateX(${100 * index}%) translateY(-100%)`
                : `translateX(${100 * index}%)`
          }"
          class="single"
        >
          {{ item }}
        </span>
      </div>
      <input
        ref="el"
        class="input-raw"
        name="input-raw"
        :type="props.type"
        :required="props.required"
        autocomplete="off"
        :style="{
          fontSize: `var(--yz-font-size, 16px)`,
          height: `var(--yz-input-height, 44px)`,
          lineHeight: `var(--yz-input-height, 44px)`,
          color: mappedState['color.fontPattern'].value,
          padding: isTypeUnderline ? '0.6em 0' : '0.6em',
          border: `0px solid transparent`
        }"
        @focus="focus"
        @blur="blur"
        @input="input"
        @change="input"
      />
      <div class="input-slot"><slot></slot></div>
      <!-- background color and height -->
      <div
        class="background"
        v-if="!isTypeUnderline"
        :style="{
          transition: props.duration + 'ms',
          backgroundColor:
            isTypeNormal || isFocus || inputText
              ? 'transparent'
              : mappedState['color.gapPattern'].value,
          height:
            isTypeNormal || isFocus || inputText
              ? `var(--yz-input-height, 44px)`
              : '2px',
          borderRadius: `var(--yz-input-border-radius, 4px)`
        }"
      ></div>
    </div>
    <!-- error prompt bottom -->
    <div
      v-if="props.showError && errorText && isTypeUnderline"
      class="error-prompt-bottom"
    >
      <Text
        type="warn"
        :color="mappedState['color.redCommon'].value"
      >
        {{ errorText }}
      </Text>
    </div>
  </div>
</template>

<style scoped>
.input {
  position: relative;
  box-sizing: border-box;
  font-size: var(--yz-font-size, 16px);
  overflow: hidden;
}
/*
.error-prompt-top {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  font-size: 0.75em;
  line-height: 1;
  transform: translateY(-100%);
}
*/
.error-prompt-top {
  width: 100%;
  font-size: 0.75em;
  line-height: 1;
  margin-bottom: 0.5em;
}
.error-prompt-bottom {
  width: 100%;
  font-size: 0.75em;
  line-height: 1;
  margin-top: 0.5em;
}
.input-wrap {
  position: relative;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.input-raw {
  flex: 1 1 auto;
  display: block;
  box-sizing: border-box;
  position: relative;
  background: transparent;
  outline: none;
  box-shadow: none;
  z-index: 10;
}
.input-slot {
  flex: 0 0 auto;
}
.background {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  pointer-events: none;
  z-index: 9;
}
.placeholder {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  pointer-events: none;
}
.focus .placeholder {
  font-size: 0.75em;
}
.placeholder > div {
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
}
.placeholder > span.single {
  position: absolute;
  left: 0;
  top: 0;
}
/*
input:-moz-placeholder,
input:-ms-input-placeholder,
input::-webkit-input-placeholder,
input::placeholder{
  color: var(--yz-gap-common, #cccccc);
}
*/
</style>
